import { Loader2, Trash2 } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { ADD_TO_YARUTA_COPY } from '@/constants/addToYarutaCopy';
import type { UploadKind } from '@/lib/uploadKind';

export interface DiscardEmailModalProps {
  open: boolean;
  /** Runs the discard. The modal stays open (showing a spinner) while `loading`. */
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
  /**
   * The line under the title. Defaults to the couldn't-read copy (YAR-140
   * AC7); Review Items passes AC3's, which also warns about the items.
   */
  body?: string;
  /**
   * A photo or file the parent added (YAR-556): the title names it and the
   * default line drops the Gmail reassurance, which would be false.
   */
  upload?: UploadKind | null;
}

const COULDNT_READ_BODY = 'You can still find it in Gmail.';
const EMAIL_TITLE = 'Discard this email?';

/**
 * Discard confirmation for an email, shared by the Review feed's couldn't-read
 * card (YAR-477 scope 3; Figma Review Flow "Default / discard couldn't read",
 * 11002:178280) and the Review Items screen (YAR-140 AC3). Only the body
 * differs: a couldn't-read email has no items to lose, so it only reassures
 * that the original stays in Gmail — discarding removes it from Review,
 * nothing else (YAR-140 AC6).
 */
export function DiscardEmailModal({
  open,
  onConfirm,
  onCancel,
  loading = false,
  body,
  upload = null,
}: DiscardEmailModalProps) {
  const title = upload ? ADD_TO_YARUTA_COPY.discardTitle(upload) : EMAIL_TITLE;
  const line =
    body ??
    (upload ? ADD_TO_YARUTA_COPY.discardCouldntReadBody : COULDNT_READ_BODY);
  return (
    <Modal
      open={open}
      onClose={onCancel}
      ariaLabel={title}
      bare
    >
      <div className="flex flex-col items-center gap-4 p-5">
        <Trash2
          size={32}
          aria-hidden="true"
          className="text-icon-default"
        />
        <div className="flex flex-col gap-1.5 text-center w-full">
          <p className="m-0 text-lg font-medium text-text-heading">{title}</p>
          <p className="m-0 text-base font-normal text-text-body">{line}</p>
        </div>
        <div className="flex flex-col gap-3 w-full">
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            aria-busy={loading}
            className="w-full flex items-center justify-center gap-1.5 rounded-md border border-border-error bg-card px-2 py-1.5 text-base font-semibold text-text-error cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-border-focus"
          >
            {loading ? (
              <Loader2
                size={16}
                aria-label="Discarding"
                className="animate-spin"
              />
            ) : (
              'Discard'
            )}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="w-full flex items-center justify-center rounded-md border-0 bg-transparent px-2 py-1.5 text-base font-semibold text-muted-foreground cursor-pointer focus-visible:outline-2 focus-visible:outline-border-focus"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}
